
    import {Component, Inject, Vue} from 'vue-property-decorator'
    import {mapGetters} from 'vuex'

    // Components
    import Layout from '@/app/common/components/layout/layout.vue'
    import ProductSigningStatus from '@/app/product/components/product-signing-status.vue'
    import PdfInteractiveViewer from '@/app/product/components/pdf-interactive-viewer.vue'

    // Entities
    import {ProductAction} from '@/app/product/entities/product-action.entity'
    import {HstdInitUrl} from '@/app/product/entities/htsd-init-url.entity'
    import {SigningProcessStatus} from '@/app/product/entities/signing-process-status.entity'
    import {SigningProcessConsumer} from '@/app/product/entities/signing-process-consumer.entity'

    // Commands
    import {GetAllActionsCommand} from '@/app/product/commands/get-all-actions.command'
    import {GetEidasDocumentCommand} from '@/app/product/commands/get-eidas-document.command'
    import {SaveFilledDocumentCommand} from '@/app/product/commands/save-filled-document.command'

    // Services
    import {AxiosClient} from '@/app/common/services/http/axios-client.service'
    import {PdfFormService} from '@/app/product/services/pdf-form.service'
    import SimpleTable from "@/app/common/components/simple-table.vue";

    @Component({
        components: {SimpleTable, Layout, ProductSigningStatus, PdfInteractiveViewer},
        metaInfo() {
            return {title: 'Document digitaal ondertekenen'}
        },
        computed: {
            ...mapGetters('product', ['getByProductActionId'])
        }
    })
    export default class SignDocumentDigitally extends Vue {
        // ===== API ENDPOINTS =====
        private static readonly HSTD_AUTH_INIT_ENDPOINT_SERVICE = '/api/goed-idee/initiate-hstd-authentication'
        private static readonly HSTD_AUTH_INIT_ENDPOINT_CREDENTIAL = '/api/goed-idee/initiate-hstd-authentication-credential'
        private static readonly SIGNING_PROCESS_STATUS_ENDPOINT = '/api/goed-idee/signing-process-status'

        // ===== TIMER INTERVALS =====
        private static readonly STATUS_CHECK_INTERVAL = 5000 // 5 seconds - backend status check
        private static readonly COUNTDOWN_UPDATE_INTERVAL = 1000 // 1 second - countdown display

        // ===== COMPONENT STATE =====
        private isSigning = false
        private signingError = false
        private productAction: ProductAction | null = null
        private signingProcessStatus: SigningProcessStatus | null = null
        private currentTime = new Date()
        private isLoadingDocument = false
        private documentLoadError = false

        // ===== STATUS CHECKING STATE =====
        private isCheckingStatus = false
        private isInitialStatusCheck = true
        private statusCheckInterval: number | null = null

        // ===== COUNTDOWN TIMER STATE =====
        private countdownInterval: number | null = null

        // ===== FORM FIELDS STATE =====
        private pdfBytes: ArrayBuffer | null = null
        private documentAlreadySigned = false
        private hasFormFields = false
        private isSavingForm = false
        private isFormDirty = false
        private hasSavedFormData = false
        private formSavedMessage = false
        private formSaveError = false
        private pdfFormService = new PdfFormService()

        // ===== COMPUTED PROPERTIES =====
        get getByProductActionId(): (id: string) => ProductAction | undefined {
            return this.$store.getters['product/getByProductActionId']
        }

        // ===== SIGNING PROCESS STATUS COMPUTED =====
        get isSigningProcessStarted(): boolean {
            return this.signingProcessStatus?.isProcessStarted ?? false
        }

        get isSigningProcessCompleted(): boolean {
            return this.signingProcessStatus?.isProcessCompleted ?? false
        }

        get isSigningProcessExpired(): boolean {
            return this.signingProcessStatus?.isProcessExpired ?? false
        }

        get isSigningProcessAborted(): boolean {
            return this.signingProcessStatus?.isProcessAborted ?? false
        }

        get isSameConsumer(): boolean {
            return this.signingProcessStatus?.isSameConsumer ?? false
        }

        get firstConsumer(): SigningProcessConsumer | null {
            return this.signingProcessStatus?.consumers?.[0] ?? null
        }

        get secondConsumer(): SigningProcessConsumer | null {
            return this.signingProcessStatus?.consumers?.[1] ?? null
        }

        // ===== COUNTDOWN COMPUTED =====
        get countdownTime(): string {
            if (!this.signingProcessStatus?.signingProcessStartedAt) {
                return ''
            }

            const startTime = new Date(this.signingProcessStatus.signingProcessStartedAt)
            const elapsed = this.currentTime.getTime() - startTime.getTime()
            const remaining = Math.max(0, 3600000 - elapsed) // 1 hour = 3600000ms

            const minutes = Math.floor(remaining / 60000)
            const seconds = Math.floor((remaining % 60000) / 1000)

            return `${minutes}:${seconds.toString().padStart(2, '0')}`
        }

        // ===== BUTTON COMPUTED =====
        get isButtonDisabled(): boolean {
            return (
                !this.productAction ||
                (this.isSameConsumer && this.isSigningProcessCompleted) ||
                (!this.isSameConsumer && this.isSigningProcessStarted) ||
                (this.isCheckingStatus && this.isInitialStatusCheck)
            )
        }

        get buttonText(): string {
            if (this.isSameConsumer && (this.isSigningProcessAborted || this.isSigningProcessExpired)) {
                return this.$t('productaction.sign_digitally_restart') as string
            } else if (this.isSameConsumer && this.isSigningProcessStarted) {
                return this.$t('productaction.sign_digitally_now') as string
            }

            return this.$t('productaction.sign_digitally_start') as string

        }

        // ===== INJECTED DEPENDENCIES =====
        @Inject() private getAllActionsCommand!: GetAllActionsCommand
        @Inject() private getEidasDocumentCommand!: GetEidasDocumentCommand
        @Inject() private saveFilledDocumentCommand!: SaveFilledDocumentCommand

        // ===== LIFECYCLE HOOKS =====
        async mounted(): Promise<void> {
            try {
                await this.initializeComponent()
                // Check status immediately to handle returning users
                await this.checkSigningProcessStatus()
                // Mark initial check as complete
                this.isInitialStatusCheck = false
                // Then start the interval for ongoing monitoring
                this.startStatusChecking()
            } catch (error) {
                console.error('Failed to initialize component:', error)
            }
        }

        beforeDestroy(): void {
            this.stopStatusChecking()
            this.stopCountdownTimer()
        }

        // ===== PUBLIC METHODS =====
        async handleSignDigitally(): Promise<void> {
            if (!this.productAction) {
                console.warn('No product action available for signing')
                return
            }

            this.signingError = false

            try {
                this.isSigning = true
                await this.signDigitally()
            } catch (error) {
                console.error('Failed to sign digitally:', error)
                this.signingError = true
            } finally {
                this.isSigning = false
            }
        }

        async handleSaveForm(): Promise<void> {
            if (!this.productAction?.id || !this.pdfBytes) {
                return
            }

            this.isSavingForm = true
            this.formSaveError = false
            this.formSavedMessage = false

            try {
                // Read values directly from the interactive PDF viewer
                const viewer = this.$refs.pdfViewer as any
                const fieldValues = viewer?.getValues() ?? {}

                // Fill the PDF with values using pdf-lib
                const filledPdfBytes = await this.pdfFormService.fillAndSerialize(this.pdfBytes, fieldValues)

                // Convert to base64
                const base64 = await this.uint8ArrayToBase64(filledPdfBytes)

                // Send to backend
                await this.saveFilledDocumentCommand.run(this.productAction.id, base64, fieldValues)

                this.isFormDirty = false
                this.formSavedMessage = true
            } catch (error) {
                console.error('Failed to save form fields:', error)
                this.formSaveError = true
            } finally {
                this.isSavingForm = false
            }
        }

        private uint8ArrayToBase64(bytes: Uint8Array): Promise<string> {
            return new Promise((resolve, reject) => {
                const blob = new Blob([bytes])
                const reader = new FileReader()
                reader.onloadend = () => {
                    const dataUrl = reader.result as string
                    resolve(dataUrl.split(',')[1])
                }
                reader.onerror = reject
                reader.readAsDataURL(blob)
            })
        }

        // ===== STATUS CHECKING METHODS =====
        private startStatusChecking(): void {
            if (this.statusCheckInterval) {
                clearInterval(this.statusCheckInterval)
            }

            this.statusCheckInterval = window.setInterval(() => {
                this.checkSigningProcessStatus()
            }, SignDocumentDigitally.STATUS_CHECK_INTERVAL)
        }

        private stopStatusChecking(): void {
            if (this.statusCheckInterval) {
                clearInterval(this.statusCheckInterval)
                this.statusCheckInterval = null
            }
        }

        // ===== COUNTDOWN TIMER METHODS =====
        private startCountdownTimer(): void {
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval)
            }

            this.countdownInterval = window.setInterval(() => {
                // Update current time to trigger reactivity
                this.currentTime = new Date()
            }, SignDocumentDigitally.COUNTDOWN_UPDATE_INTERVAL)
        }

        private stopCountdownTimer(): void {
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval)
                this.countdownInterval = null
            }
        }

        // ===== API METHODS =====
        private async checkSigningProcessStatus(): Promise<void> {
            if (!this.productAction?.id) {
                return
            }

            this.isCheckingStatus = true

            try {
                const client = new AxiosClient()
                const config = {
                    params: {
                        productActionId: this.productAction.id
                    }
                }

                const response = await client.get(SignDocumentDigitally.SIGNING_PROCESS_STATUS_ENDPOINT, config)
                const statusDataArray = response.asEntity(SigningProcessStatus)

                // Update the status - take the first item from the array
                this.signingProcessStatus = Array.isArray(statusDataArray) ? statusDataArray[0] : statusDataArray

                // Start or stop countdown timer based on status
                if (this.signingProcessStatus?.isProcessStarted && !this.signingProcessStatus?.isProcessCompleted && !this.signingProcessStatus?.isProcessExpired && !this.signingProcessStatus?.isProcessAborted) {
                    this.startCountdownTimer()
                } else {
                    this.stopCountdownTimer()
                }

                // If process is completed, expired, or aborted, stop checking
                if (this.signingProcessStatus?.isProcessCompleted || this.signingProcessStatus?.isProcessExpired || this.signingProcessStatus?.isProcessAborted) {
                    this.stopStatusChecking()
                }
            } catch (error) {
                console.error('Failed to check signing process status:', error)
            } finally {
                this.isCheckingStatus = false
            }
        }

        // ===== INITIALIZATION METHODS =====
        private async initializeComponent(): Promise<void> {
            await this.getAllActionsCommand.run()
            this.productAction = this.getByProductActionId(this.$route.params.action) ?? null

            // Initialize document URL for PDF viewer
            if (this.productAction) {
                await this.initializeDocumentUrl()
            }
        }

        private async initializeDocumentUrl(): Promise<void> {
            if (!this.productAction?.id) {
                return
            }

            this.isLoadingDocument = true
            this.documentLoadError = false
            this.pdfBytes = null

            try {
                const result = await this.getEidasDocumentCommand.run(this.productAction.id)
                this.pdfBytes = result.pdfBytes
                this.hasSavedFormData = result.hasSavedFormData
                this.documentAlreadySigned = result.isSigned
            } catch (error) {
                console.error('Failed to load eIDAS document:', error)
                this.documentLoadError = true
                this.pdfBytes = null
            } finally {
                this.isLoadingDocument = false
            }
        }

        private onPdfLoaded(hasFields: boolean): void {
            this.hasFormFields = hasFields && !this.documentAlreadySigned
            this.isFormDirty = this.hasFormFields
        }

        // ===== SIGNING METHODS =====
        private async signDigitally(): Promise<void> {
            const hstdInitUrl = await this.fetchRedirectUrl()

            if (hstdInitUrl?.url) {
                window.location.href = hstdInitUrl.url
            } else {
                throw new Error('No redirect URL received from server')
            }
        }

        private async fetchRedirectUrl(): Promise<HstdInitUrl> {
            const client = new AxiosClient()
            const config = {
                params: {
                    productActionId: this.productAction?.id
                }
            }

            // If process was aborted, always use service endpoint to start fresh
            let endpoint: string
            if (this.isSigningProcessAborted || this.isSigningProcessExpired) {
                endpoint = SignDocumentDigitally.HSTD_AUTH_INIT_ENDPOINT_SERVICE
            } else if (this.productAction?.documentSigning?.hasCertificate) {
                endpoint = SignDocumentDigitally.HSTD_AUTH_INIT_ENDPOINT_CREDENTIAL
            } else {
                endpoint = SignDocumentDigitally.HSTD_AUTH_INIT_ENDPOINT_SERVICE
            }

            const response = await client.get(endpoint, config)

            return response.asEntity(HstdInitUrl)
        }
    }
